import React from 'react'
import { useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { BREAKPOINTS, fontVariables, smallBreakpointFontVariables } from '@zoe/ds-web'

/* The app shell: a viewport-tall flex column of scrolling content (header
   included) / footer.

   Why the content scrolls in its own region rather than the window (WEB-895):
   in Chrome on iPhone, once the window scrolls and the browser collapses its
   toolbars, a CTA pinned to the bottom of the screen (fixed or sticky) paints in
   one place but takes taps somewhere else, until the user scrolls back up. With
   the window never scrolling, the toolbars never collapse, and the footer is a
   plain flex sibling of the scroll region rather than an overlay.

   dvh, not vh: vh resolves to the toolbar-collapsed height, which would push the
   footer below the visible area while a mobile browser's toolbars are showing. */
/* The design system declares its type scale on <html> at desktop sizes and
   leaves the phone sizes to the app. Re-declaring fontVariables here, before the
   phone overrides, recomputes the line heights from the smaller font sizes. */
const phoneTypeScale = css`
  @media (max-width: calc(${BREAKPOINTS.m} - 1px)) {
    ${fontVariables}
    ${smallBreakpointFontVariables}
  }
`

const Shell = styled.div<{ $background?: string; $phoneTypeScale?: boolean }>`
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${({ $background }) => $background ?? 'transparent'};
  ${({ $phoneTypeScale }) => ($phoneTypeScale ? phoneTypeScale : '')}
`

/* min-height: 0 lets this shrink below its content's intrinsic height, which a
   flex item otherwise refuses to do — without it the region grows to fit the
   content and pushes the footer off-screen instead of scrolling. */
const ScrollRegion = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
`

const FooterSlot = styled.div`
  flex: none;
`

/* Where StickyFooter portals to, so footers declared deep inside a screen
   (inside <form>, inside ScreenContainer) still render outside the scroller.
   undefined: no shell. null: shell mounted, slot not attached yet. */
const FooterSlotContext = React.createContext<HTMLElement | null | undefined>(undefined)

export const useFooterSlot = (): HTMLElement | null | undefined => React.useContext(FooterSlotContext)

type Props = {
  header?: React.ReactNode
  children: React.ReactNode
  backgroundColor?: string
  phoneTypeScale?: boolean
}

export const ScreenShell: React.FC<Props> = ({ header, children, backgroundColor, phoneTypeScale }) => {
  const { pathname } = useLocation()
  const scrollRegion = React.useRef<HTMLDivElement>(null)
  const [footerSlot, setFooterSlot] = React.useState<HTMLDivElement | null>(null)

  /* Reset scroll on every route change. Consecutive screens often share a
     component tree, so React reconciles instead of remounting and the previous
     screen's offset would otherwise persist. Layout effect so the new screen
     never paints at the old offset. */
  React.useLayoutEffect(() => {
    if (scrollRegion.current) scrollRegion.current.scrollTop = 0
  }, [pathname])

  return (
    <FooterSlotContext.Provider value={footerSlot}>
      <Shell $background={backgroundColor} $phoneTypeScale={phoneTypeScale}>
        {/* The header scrolls with the content, as it did before the shell: it
           frees the space on small phones, and overlayHeader screens pull their
           hero image up behind it, which the scroller would otherwise clip. */}
        <ScrollRegion ref={scrollRegion} data-testid="screen-scroll-region">
          {header}
          {children}
        </ScrollRegion>
        <FooterSlot ref={setFooterSlot} />
      </Shell>
    </FooterSlotContext.Provider>
  )
}
